import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  beginGoogleLogin,
  beginLichessLogin,
  getTurnstileConfig,
  signup,
  type TurnstileConfig,
} from "@/pages/auth/api";
import { OAuthButton } from "@/pages/auth/OAuthButton";
import { persistSession } from "@/pages/auth/storage";
import { TurnstileWidget } from "@/pages/auth/TurnstileWidget";
import { useI18n } from "@/shared/i18n/LanguageContext";

export function SignupPage() {
  const { copy } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileConfig, setTurnstileConfig] = useState<TurnstileConfig | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getTurnstileConfig()
      .then((config) => {
        if (!cancelled) {
          setTurnstileConfig(config);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTurnstileConfig({ enabled: false, site_key: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const submitSignup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (turnstileConfig?.enabled && !turnstileToken) {
      setError(copy.auth.securityRequired);
      return;
    }
    setIsSubmitting(true);

    try {
      const session = await signup({
        email: form.email.trim(),
        username: form.username.trim(),
        password: form.password,
        confirm_password: form.confirmPassword,
        turnstile_token: turnstileToken,
      });
      persistSession(session);
      navigate(`/@/${session.user.username}`);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : copy.auth.signupFailed,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <form className="auth-panel" onSubmit={submitSignup}>
        <h1 className="auth-panel__title">
          {copy.auth.signupTitle}
        </h1>

        <div className="field-grid">
          <div className="field">
            <label htmlFor="signup-email">{copy.auth.email}</label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={(event) =>
                setForm((current) => ({ ...current, email: event.target.value }))
              }
            />
          </div>

          <div className="field">
            <label htmlFor="signup-username">{copy.auth.username}</label>
            <input
              id="signup-username"
              type="text"
              autoComplete="username"
              value={form.username}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  username: event.target.value.toLowerCase(),
                }))
              }
            />
          </div>

          <div className="field">
            <label htmlFor="signup-password">{copy.auth.password}</label>
            <input
              id="signup-password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(event) =>
                setForm((current) => ({ ...current, password: event.target.value }))
              }
            />
          </div>

          <div className="field">
            <label htmlFor="signup-confirm">{copy.auth.confirmPassword}</label>
            <input
              id="signup-confirm"
              type="password"
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  confirmPassword: event.target.value,
                }))
              }
            />
          </div>
        </div>

        {turnstileConfig?.enabled && turnstileConfig.site_key ? (
          <TurnstileWidget
            siteKey={turnstileConfig.site_key}
            onTokenChange={setTurnstileToken}
          />
        ) : null}

        {error ? <p className="auth-panel__error">{error}</p> : null}

        <div className="auth-panel__actions">
          <button
            className="button-link button-link--primary"
            type="submit"
            disabled={isSubmitting || (turnstileConfig?.enabled === true && !turnstileToken)}
          >
            {isSubmitting ? copy.auth.creating : copy.auth.createAccount}
          </button>

          <OAuthButton provider="google" onClick={beginGoogleLogin} />
          <OAuthButton provider="lichess" onClick={beginLichessLogin} />
        </div>

        <p className="auth-panel__linkline">
          {copy.auth.existingUser} <Link to="/login">{copy.auth.loginLink}</Link>
        </p>
      </form>
    </div>
  );
}
